<template>
    <div class="tree-item-wrapper">
        <div class="tree-item d-flex align-items-center px-3 py-1" @click.prevent="handleTreeItem()">
            <c-checkbox :value="selected" />
            <div class="d-flex flex-grow-1 w40" :class="{ 'level-1': hasChildren ? true : false }">
                <span class="ml-2">{{ treeItem.name }}</span>
            </div>
            <c-dropdown
                v-if="!hasChildren"
                class="w-50"
                :options="getElementTypes"
                :value="treeItem.type"
                :name="'treeType'"
                placeholder="Select Element Type"
                @input="onTreeItemTypeChange(treeItem, $event)"
            />
        </div>
        <modal-import-layers-tree-item-external
            v-for="(item, index) in treeItem.paginatedChildren"
            :key="`tree-item-${index}`"
            :tree-item="item"
            :tree-item-index="index"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import store from '@/store';
import { TreeItem, TreeType } from '@/components/Layers/modal/types';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import CDropdown from '@/components/CDropdown.vue';

const props = defineProps<{
    treeItem: TreeItem;
    treeItemIndex?: number;
}>();

const getElementTypes = [
    {
        value: TreeType.FROM_FILE_TEXT_ELEMENT,
        text: 'Text'
    },
    {
        value: TreeType.FROM_FILE_IMAGE_PLACEHOLDER_ELEMENT,
        text: 'Image Placeholder'
    },
    {
        value: TreeType.FROM_FILE_IMAGE_ELEMENT,
        text: 'Dynamic Image'
    },
    {
        value: TreeType.FROM_FILE_VECTOR_PLACEHOLDER_ELEMENT,
        text: 'Vector Placeholder'
    },
    {
        value: TreeType.FROM_FILE_VECTOR_ELEMENT,
        text: 'Dynamic Vector'
    }
];

const hasChildren = computed(() => props.treeItem.children?.length);

const selected = computed({
    get() {
        if (props.treeItem.children && props.treeItem.children.length)
            return store.state.storeLayersModal!.selectedTreeItems.length === props.treeItem.children.length;

        return store.state.storeLayersModal!.selectedTreeItems.some((item) => {
            return item.id === props.treeItem.id;
        });
    },
    set(value: boolean) {
        const handleTreeItem = (treeItem: TreeItem) => {
            if (value) {
                store.dispatch('storeLayersModal/addSelectedTreeItem', treeItem);
            } else {
                store.dispatch('storeLayersModal/removeSelectedTreeItem', treeItem);
            }
        };

        const methods: Record<string, (treeItem: TreeItem) => void> = {
            [TreeType.FROM_FILE_TEXT_ELEMENT]: handleTreeItem,
            [TreeType.FROM_FILE_IMAGE_PLACEHOLDER_ELEMENT]: handleTreeItem,
            [TreeType.FROM_FILE_IMAGE_ELEMENT]: handleTreeItem,
            [TreeType.FROM_FILE_VECTOR_PLACEHOLDER_ELEMENT]: handleTreeItem,
            [TreeType.FROM_FILE_VECTOR_ELEMENT]: handleTreeItem,
            default: (treeItem) => {
                if (!hasChildren.value) methods[treeItem.type](treeItem);
                treeItem.children?.forEach((item: TreeItem) => {
                    methods[item.type](item);
                });
            }
        };

        const method = methods[props.treeItem.type] || methods['default'];

        method(props.treeItem);
    }
});

const handleTreeItem = () => {
    selected.value = !selected.value;
};

const onTreeItemTypeChange = (treeItem: TreeItem, type: TreeType) => {
    treeItem.type = type;
};
</script>

<style lang="scss" scoped>
.tree-item-wrapper {
    .tree-item {
        margin-left: 0;
        &:hover {
            font-weight: bold;
            background-color: #f0f1f2 !important;
            cursor: pointer;
        }
        .avatar {
            background-color: #fff !important;
            border: 0.1rem solid rgba(225, 227, 229, 1) !important;
        }
        .level-1 {
            margin-left: 0.5rem !important;
        }
    }
}
</style>
